import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Mail, ExternalLink } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({});

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/settings/`, { withCredentials: true })
      .then(res => setSettings(res.data || {}))
      .catch(() => {});
  }, []);

  const currentYear = new Date().getFullYear();
  const siteName = settings.site_name || 'İnsan Yayınları';
  const socialLinks = settings.social_links || {};

  return (
    <footer className="relative" style={{ backgroundColor: 'var(--theme-nav-bg, #1A1A1A)', color: '#A8A6A0' }}>
      {/* Top divider */}
      <div className="h-px" style={{ backgroundColor: 'var(--theme-primary, #7A2E24)', opacity: 0.4 }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">

          {/* Brand */}
          <div className="md:col-span-4">
            <Link to="/" className="inline-block">
              <h3 className="text-2xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'var(--theme-font-heading, "Cormorant Garamond", serif)' }}>
                {siteName}
              </h3>
            </Link>
            <p className="text-sm leading-relaxed max-w-xs" style={{ fontFamily: 'var(--theme-font-body)', color: '#A8A6A0' }}>
              {settings.site_description || 'Bilgi ve kültür yayıncılığında yarım asırlık tecrübe.'}
            </p>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-white mb-5">Keşfet</h4>
            <ul className="space-y-3">
              {[
                { to: '/books', label: t('nav.books', 'Kitaplar') },
                { to: '/authors', label: t('nav.authors', 'Yazarlar') },
                { to: '/series', label: t('nav.series', 'Diziler') },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm hover:text-white transition-colors duration-200" style={{ color: '#A8A6A0' }}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-white mb-5">Kurumsal</h4>
            <ul className="space-y-3">
              {[
                { to: '/hakkimizda', label: 'Hakkımızda' },
                { to: '/iletisim', label: 'İletişim' },
                { to: '/kvkk', label: 'KVKK' },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm hover:text-white transition-colors duration-200" style={{ color: '#A8A6A0' }}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-white mb-5">İletişim</h4>
            <div className="space-y-3 text-sm">
              {settings.address && (
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--theme-primary, #7A2E24)' }} />
                  <span>{settings.address}</span>
                </div>
              )}
              {settings.phone && (
                <div className="flex items-center gap-3">
                  <Phone size={16} style={{ color: 'var(--theme-primary, #7A2E24)' }} />
                  <a href={`tel:${settings.phone}`} className="hover:text-white transition-colors">{settings.phone}</a>
                </div>
              )}
              {settings.email && (
                <div className="flex items-center gap-3">
                  <Mail size={16} style={{ color: 'var(--theme-primary, #7A2E24)' }} />
                  <a href={`mailto:${settings.email}`} className="hover:text-white transition-colors">{settings.email}</a>
                </div>
              )}
            </div>

            {/* Social */}
            {Object.keys(socialLinks).length > 0 && (
              <div className="flex gap-4 mt-6">
                {Object.entries(socialLinks).filter(([, v]) => v).map(([key, url]) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 flex items-center justify-center border border-gray-700 hover:border-white hover:text-white transition-all duration-200 text-xs uppercase font-mono"
                    style={{ borderRadius: 'var(--theme-radius, 0px)' }}>
                    {key.charAt(0).toUpperCase()}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs" style={{ color: '#666' }}>
            &copy; {currentYear} {siteName}. Tüm hakları saklıdır.
          </p>
          <p className="text-xs" style={{ color: '#444' }}>
            Powered by İnsan Yayınları Dijital
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
